/**
 * Map VoiceNoteFxParams → MIDI CCs (parity with clip/exportMidi.ts).
 * pan → CC10, vibrato depth → CC1, filter/cutoff → CC74.
 */
import type { VoiceNoteFxParams } from "../types";

export type MidiCcEvent = {
  /** Controller number 0–127. */
  controller: number;
  /** 0–1 normalized value (caller scales to 0–127). */
  value01: number;
};

/**
 * Build export-parity CC list from decoded note FX (or undefined when none).
 */
export function midiCcFromNoteFx(
  noteFx: VoiceNoteFxParams | undefined,
): MidiCcEvent[] {
  if (!noteFx) {
    return [];
  }
  const out: MidiCcEvent[] = [];
  if (typeof noteFx.pan === "number" && Number.isFinite(noteFx.pan)) {
    const value01 = Math.min(1, Math.max(0, (noteFx.pan + 1) / 2));
    // Near center (~B80) — export skips; keep a small deadband.
    if (Math.abs(value01 - 0.5) > 0.02) {
      out.push({ controller: 10, value01 });
    }
  }
  if (
    typeof noteFx.vibratoDepthSemi === "number"
    && Number.isFinite(noteFx.vibratoDepthSemi)
    && noteFx.vibratoDepthSemi > 0
  ) {
    // Decode: depthNibble * 0.35; export: depth/15.
    const value01 = Math.min(1, noteFx.vibratoDepthSemi / (0.35 * 15));
    if (value01 > 0) {
      out.push({ controller: 1, value01 });
    }
  }
  if (typeof noteFx.cutoffOct === "number" && Number.isFinite(noteFx.cutoffOct)) {
    // Inverse of decode: cutoffOct = ((v/128)-1)*2 → v = (cutoffOct/2+1)*128
    const byte = Math.min(255, Math.max(0, Math.round((noteFx.cutoffOct / 2 + 1) * 128)));
    if (Math.abs(byte - 0x80) > 4) {
      out.push({ controller: 74, value01: byte / 255 });
    }
  }
  return out;
}

/** Clamp MIDI note / velocity bytes. */
export function clampMidi7(value: number): number {
  return Math.min(127, Math.max(0, Math.round(value)));
}
